import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import ContextBar from '../components/common/ContextBar';
import NameDashboardModal from '../components/reports/NameDashboardModal';
import { useCollectionContext } from '../contexts/CollectionContext';
import { createDashboard } from '../services/dashboards';
import { collectionCrumbs } from '../utils/breadcrumbs';
import { dashboardPath } from '../utils/dashboards';
import { normalizeId } from '../utils/ids';
import { canReadCharts } from '../utils/permissions';

/**
 * A new dashboard — `/collections/:collectionId/dashboards/new` (charts A6,
 * plan §6.5): its own bar (the trail to Reports, "New dashboard") and the
 * naming window. **Create** makes the dashboard — always the person's own;
 * a manager pins it for everyone afterwards — and opens it, empty, in
 * Arrange. Cancel goes back to Reports.
 */
export default function NewDashboardPage() {
	const { collection, resolvedCollectionId } = useCollectionContext() || {};
	const collectionId = resolvedCollectionId;
	const navigate = useNavigate();
	const reportsPath = `/collections/${collectionId}/reports`;
	const allowed = Boolean(collection) && canReadCharts(collection);
	// The window closes itself after a create too: only a Cancel goes back.
	const created = useRef(false);

	const create = async (values) => {
		const dashboard = await createDashboard(collectionId, values);
		created.current = true;
		navigate(dashboardPath(collectionId, normalizeId(dashboard?._id)), { replace: true, state: { arrange: true } });
	};

	return (
		<div className='dashboard-page dashboard-page--new'>
			<ContextBar
				breadcrumb={collectionCrumbs(collection, collectionId, { label: 'Reports', to: reportsPath })}
				title='New dashboard'
				menuActions={[{ key: 'reports', group: 'navigate', glyph: 'open', label: 'Go to Reports', onClick: () => navigate(reportsPath) }]}
			/>
			<div className='dashboard-page__body'>
				{collection && !allowed ? (
					<p className='dashboard-page__empty'>Charts are for people who read this collection themselves, so there is none to show here.</p>
				) : null}
			</div>
			<NameDashboardModal
				open={Boolean(collectionId) && (!collection || allowed)}
				mode='create'
				onSubmit={create}
				onClose={() => { if (!created.current) navigate(reportsPath); }}
			/>
		</div>
	);
}
